import { useEffect } from 'react';
import type { ReactNode } from 'react';

// The long-form case for the project ("Read the argument" on the landing
// page). It opens with how coding agents get library knowledge today, then
// makes the argument at full length and adds what marketing copy cannot:
// method, measured results, and limits.
//
// The FastHTML walkthrough's numbers come from the same eval runs as "What we
// measured": 11 of 15 from-memory FastHTML projects (three runs × five models)
// missed at least one required FastHTML pattern, per the judge's verdicts.
//
// Every number in "What we measured" comes from the eval/copilot-codegen.ts
// matrix runs of 2026-11-04 (eval/results/codegen-matrix-2026-11-04-generic
// and -named, and the earlier codegen-matrix-small-models-2026-11-02-v2 for the
// 19/35 baseline). Update them together with a new run, never by hand alone.

const SLOPSQUATTING_RESEARCH_URL =
  'https://www.trendaisecurity.com/en-us/resources-insights/deep-research/slopsquatting-when-ai-agents-hallucinate-malicious-packages';

const CONTENTS = [
  ['today', '1. How a coding agent works today'],
  ['problem', '2. Recall is not lookup'],
  ['library', '3. A reference library for agents'],
  ['small', '4. Small questions, small models'],
  ['private', '5. Private code, local models'],
  ['evidence', '6. What we measured'],
  ['limits', '7. What it does not do yet'],
  ['open', '8. Open, as the plan'],
  ['references', 'References'],
] as const;

const TODAY_STEPS = [
  [
    'It writes from memory.',
    'The model starts from what it learned in training. For a young framework that resembles older ones, memory drifts to the familiar shape.',
  ],
  [
    'It searches the web.',
    'With web tools, it searches and lands on whatever ranks: a tutorial for another version, a forum answer, a page that never says which release it describes. Fetching one documentation page costs roughly 5,000–15,000 tokens of context, most of it navigation.',
  ],
  [
    'It reads the installed source.',
    'It installs the package and greps through site-packages to reconstruct the API: roughly 10,000–40,000 tokens spent rereading code that every earlier session already read.',
  ],
  [
    'It runs, fails, and patches.',
    'It runs the code, reads the traceback, and edits. That catches code that crashes. It does not catch code that runs and is wrong — a deprecated option, a renamed parameter, a pattern from another framework that happens to work.',
  ],
] as const;

const LOOKUP_STEPS = [
  [
    'Skim the table of contents',
    'search_packages',
    'Every indexed package carries a contents page: the versions that exist and the titles of what is inside each one. An agent skims it first — no chunk bodies, no model call — the way a person scans an index before deciding which page to open.',
  ],
  [
    'Turn to the edition you own',
    'lockfile → package@version',
    'A library has editions, and only one of them is installed. The lockfile names it, retrieval is scoped to that exact release, and the answer describes the code the project actually runs.',
  ],
  [
    'Read the passage, not the shelf',
    'get_context',
    'One focused question comes back as the few spans that answer it, each labeled with the package@version and the file or document it came from — a page the reader can open and check.',
  ],
  [
    'The shelf restocks itself',
    'freshness watcher',
    'A watcher polls each registry, notices when a newer release is published, and queues it for indexing, so the contents page an agent reads next week describes next week’s releases.',
  ],
] as const;

const PRINCIPLES = [
  [
    'Establish “as of.”',
    'See when the package snapshot was indexed and when the release was published. Currency is evidence to inspect, not a claim.',
  ],
  [
    'Identify the version.',
    'Resolve the exact release from the lockfile or workspace pins and compare it with what is indexed.',
  ],
  [
    'Verify the source.',
    'Tie the package identity to its registry or named repository before treating its APIs and documentation as evidence.',
  ],
  ['Pin, then retrieve.', 'Retrieve only from that package@version, for the agent’s one question.'],
] as const;

const MODELS = [
  ['qwen/qwen3.5-9b', '9B', '+33 (n=3)', '+17 (n=6)'],
  ['mistralai/ministral-8b-2512', '8B', '+13 (n=5)', '+6 (n=6)'],
  ['openai/gpt-oss-20b', '20B', '+67 (n=1)', '+33 (n=2)'],
  ['qwen/qwen3-coder-30b-a3b-instruct', '30B, 3B active', '−8 (n=3)', '+25 (n=4)'],
  ['google/gemma-4-26b-a4b-it', '26B, 4B active', '+22 (n=6)', '+18 (n=5)'],
] as const;

const PROMPTS = [
  ['pydantic-ai 2.64.0', 'Python', 'CLI support assistant'],
  ['python-fasthtml 0.14.17', 'Python', 'Server-rendered reading list'],
  ['google-adk 2.13.0', 'Python', 'Book-availability agent'],
  ['stringlocale 0.2.0', 'TypeScript', 'Localized tip calculator'],
  ['zod 4.6.7', 'TypeScript', 'Signup payload validator'],
  ['axum 0.8.9', 'Rust', 'In-memory notes JSON API'],
  ['flutter_riverpod 3.4.3', 'Dart', 'Cart counter app'],
] as const;

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} aria-labelledby={`${id}-title`} className="scroll-mt-20 space-y-5">
      <h2 id={`${id}-title`} className="font-sans text-2xl font-semibold tracking-tight text-ink">
        {title}
      </h2>
      {children}
    </section>
  );
}

function P({ children }: { children: ReactNode }) {
  return <p className="font-serif text-lg leading-relaxed text-link">{children}</p>;
}

function Code({ children }: { children: ReactNode }) {
  return (
    <code className="rounded bg-code-bg px-1.5 py-0.5 font-mono text-[0.85em] text-ink">
      {children}
    </code>
  );
}

function Table({ head, rows }: { head: readonly string[]; rows: readonly (readonly string[])[] }) {
  return (
    <div className="overflow-x-auto border border-rule">
      <table className="w-full border-collapse text-left text-sm">
        <thead className="bg-bg text-xs uppercase tracking-wider text-dim">
          <tr>
            {head.map((cell) => (
              <th key={cell} scope="col" className="px-4 py-3 font-semibold">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row[0]} className="border-t border-rule">
              {row.map((cell, i) => (
                <td
                  key={i}
                  className={`px-4 py-3 align-top ${i === 0 ? 'font-mono text-xs text-ink' : 'text-link'}`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ArgumentPage() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'llmcontext — the argument';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <article className="mx-auto max-w-3xl space-y-14 px-2 py-14" data-testid="argument">
      <header className="space-y-5 border-b border-rule pb-10">
        <p className="text-xs font-semibold uppercase tracking-widest text-dim">
          The argument · November 2026
        </p>
        <h1 className="font-sans text-4xl font-semibold leading-tight tracking-tight text-ink sm:text-5xl">
          Give coding agents up-to-date information
        </h1>
        <p className="font-serif text-xl leading-relaxed text-link">
          A reference library instead of a bigger model: exact, version-pinned answers from the
          packages a project uses and its private code, asked one small question at a time.
        </p>
      </header>

      <nav aria-label="Contents" className="space-y-3">
        <p className="text-xs font-semibold uppercase tracking-widest text-dim">Contents</p>
        <ol className="grid gap-x-8 gap-y-1.5 text-sm sm:grid-cols-2">
          {CONTENTS.map(([id, label]) => (
            <li key={id}>
              <a
                href={`#${id}`}
                className="text-link underline-offset-4 hover:text-ink hover:underline"
              >
                {label}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      <Section id="today" title="1. How a coding agent works today">
        <P>
          A coding agent is a language model inside a harness &mdash; Claude Code, Cursor, GitHub
          Copilot, Codex, or one you built. The harness gives the model tools: read and write files,
          run shell commands, and in many setups search and fetch the web. The model decides what to
          do next; the harness carries it out and hands back the result. Everything the agent knows
          about a library comes from two places: what the model learned in training, and what those
          tools bring into the session.
        </P>
        <P>
          Ask one to &ldquo;build a small reading-list web app with FastHTML.&rdquo; FastHTML is a
          young Python framework built on Starlette, and it looks a lot like Flask and FastAPI. This
          is how the agent typically gets there:
        </P>
        <ol className="space-y-4">
          {TODAY_STEPS.map(([heading, body], index) => (
            <li key={heading} className="border-l-2 border-rule pl-5">
              <p className="font-sans text-base font-semibold text-ink">
                {index + 1}. {heading}
              </p>
              <p className="mt-1 font-serif text-lg leading-relaxed text-link">{body}</p>
            </li>
          ))}
        </ol>
        <P>
          The first step is where FastHTML goes wrong. In our evaluation, 11 of 15 FastHTML projects
          written from memory missed at least one of the framework&rsquo;s required patterns &mdash;
          most often by writing Flask:{' '}
          <Code>@app.route(&quot;/add&quot;, methods=[&quot;POST&quot;])</Code>,{' '}
          <Code>app.run()</Code>, and <Code>request.form.get(&quot;title&quot;)</Code> with{' '}
          <Code>request</Code> never imported. FastHTML registers routes with <Code>@rt</Code>,
          starts with <Code>serve()</Code>, and binds form fields to typed handler parameters.
        </P>
        <P>
          Each step works some of the time, and none of it is kept: the next session, on the next
          machine, for the next developer, starts over. Every step spends the model&rsquo;s context
          and the developer&rsquo;s time rediscovering what is already known &mdash; which version
          is installed, and what it accepts. Our rough estimate: about 55,000 tokens of exploratory
          reading per session, against about 3,000 for one grounded answer. That is a stated
          assumption, not a measurement of any team &mdash; the point is the shape of the cost,
          which grows with every session that rereads what is already known.
        </P>
        <P>
          Sometimes the cost is worse than waste. In a slopsquatting attack, someone registers a
          package name that models are likely to invent and waits for an agent to install it.
          TrendAI researchers observed coding agents producing plausible packages that did not exist
          and describe package hallucination as a tangible software supply-chain threat; they also
          found that live validation reduces it without eliminating every case.
          <sup>[1]</sup>
        </P>
      </Section>

      <Section id="problem" title="2. Recall is not lookup">
        <P>
          A model can know an extraordinary amount and still be wrong about the one detail that
          matters. Experienced engineers do not answer an unfamiliar API from memory either. They
          hold the shape of the thing &mdash; there is a client, it takes options, something
          configures retries &mdash; and then they look: open the docs for the version actually
          installed, find the method, read the signature. People read the paper before they act, and
          look up the details they are unsure of.
        </P>
        <P>
          Agents were given the recall and no library to walk into. A model is trained once, against
          a corpus that stops on a particular day. Software does not stop: new packages, new majors,
          renamed exports, configuration shapes that churn while the concept underneath stays still.
          Every release published after the cutoff is a fact the model cannot know it is missing,
          and from the inside a wrong field name reads exactly like a right one.
        </P>
        <P>
          Waiting for a newer model does not close the gap. It moves the cutoff, and the ecosystem
          ships the next morning. Private repositories and internal packages are worse still: they
          appear in no model&rsquo;s training data at all.
        </P>
      </Section>

      <Section id="library" title="3. A reference library for agents">
        <P>
          The reference material already exists. Lockfiles name the exact release. Registries prove
          the package is real. Documentation and source can be snapshotted with the date they were
          read. What has been missing is the building around it. llmcontext builds it the way people
          already look things up:
        </P>
        <ol className="space-y-4">
          {LOOKUP_STEPS.map(([heading, tool, body], index) => (
            <li key={heading} className="border-l-2 border-rule pl-5">
              <p className="font-sans text-base font-semibold text-ink">
                {index + 1}. {heading} <Code>{tool}</Code>
              </p>
              <p className="mt-1 font-serif text-lg leading-relaxed text-link">{body}</p>
            </li>
          ))}
        </ol>
        <P>Four rules decide what counts as evidence:</P>
        <ul className="space-y-3">
          {PRINCIPLES.map(([heading, body]) => (
            <li key={heading} className="font-serif text-lg leading-relaxed text-link">
              <strong className="font-sans text-base font-semibold text-ink">{heading}</strong>{' '}
              {body}
            </li>
          ))}
        </ul>
        <P>
          The tools are exposed over MCP, so any MCP-compatible agent &mdash; Claude Code, Cursor,
          Windsurf, VS Code, Copilot, or one you wrote &mdash; reads from the same shelf. A library
          is read once and queried by every developer, editor, and agent after that: no relearning
          every library, no scraping its documentation in every session.
        </P>
      </Section>

      <Section id="small" title="4. Small questions, small models">
        <P>
          Large models earn their size holding a large context. Most of what a coding agent needs is
          not that. It is a narrow question &mdash; what does this function take, in this version?
          &mdash; with an answer that can be checked. Split the work into questions that size and
          give each one a place to look, and small models go a long way.
        </P>
        <P>
          llmcontext runs on the same idea. Summarizing a file during indexing, choosing the
          passages that answer a question, and writing the answer are separate steps, and each is a
          model role configured on its own (<Code>OPENROUTER_INDEX_MODEL</Code>,{' '}
          <Code>OPENROUTER_SELECT_MODEL</Code>, <Code>OPENROUTER_ANSWER_MODEL</Code>), so each can
          be filled by a small model. The agent on the other side of the lookup can be small too: it
          needs to ask well and follow the evidence, not to remember every library.
        </P>
      </Section>

      <Section id="private" title="5. Private code, local models">
        <P>
          Private repositories are the code no model was trained on, and the code that should least
          leave its owners&rsquo; network. llmcontext self-hosts with Docker and Postgres; payloads
          go to a local filesystem or S3-compatible storage. Every model it uses is replaceable:
          summaries and answers go to any OpenAI-compatible server (Ollama, vLLM, LiteLLM) through{' '}
          <Code>OPENROUTER_BASE_URL</Code>, and embeddings to an open-source embedding model served
          in-network through <Code>VOYAGE_API_URL</Code>. Swapping a chat model is a configuration
          change; swapping the embedding model means a re-index, because stored vectors have a fixed
          width.
        </P>
        <P>
          With every model pointed inside the network, a private repository is indexed and queried
          without its code leaving it. Organizations, API keys, and workspace version pins control
          who and what can query each index, and every answer stays tied to a package, version,
          source, and index date that can be audited.
        </P>
      </Section>

      <Section id="evidence" title="6. What we measured">
        <P>
          We ran a GitHub Copilot SDK agent against five small open models, each building the same
          seven small projects twice: once from memory, and once with llmcontext&rsquo;s{' '}
          <Code>search_packages</Code> and <Code>get_context</Code> tools available. Each prompt
          pins a library version that changed recently enough for models to get wrong:
        </P>
        <Table head={['Package', 'Language', 'Task']} rows={PROMPTS} />
        <P>
          A judge model (Claude Haiku 5.5) graded each generated project against a per-prompt
          checklist of facts verified beforehand against the pinned version &mdash; it checks the
          code against given facts rather than recalling the API itself. We spot-checked its
          verdicts against the code.
        </P>
        <h3 className="font-sans text-lg font-semibold text-ink">Did agents look things up?</h3>
        <P>
          Under a generic instruction (&ldquo;verify package APIs before using them&rdquo;), 22 of
          35 runs retrieved any evidence; an earlier run, before we tightened the tool&rsquo;s
          parameters, managed 19 of 35. When the instruction also named the exact package and
          version to look up, 31 of 35 did. Small models skip a tool they are not pointed at &mdash;
          most often on the Rust and Dart prompts.
        </P>
        <h3 className="font-sans text-lg font-semibold text-ink">Did the code get better?</h3>
        <P>
          Change in the share of required checklist items met, in percentage points, with llmcontext
          minus without, over the prompts where both conditions produced a project the judge could
          score (<em>n</em>):
        </P>
        <Table head={['Model', 'Size', 'Generic instruction', 'Named package']} rows={MODELS} />
        <P>
          Four of five models scored higher with llmcontext under the generic instruction, and all
          five under the named one. Read these as direction, not effect size: each prompt ran once,
          the same model&rsquo;s gain moved by tens of points between runs, and the judge reads code
          rather than executing it.
        </P>
        <h3 className="font-sans text-lg font-semibold text-ink">One worked example</h3>
        <P>
          Asked for a notes API on axum 0.8.9, most models wrote <Code>/notes/:id</Code> from memory
          &mdash; the axum 0.7 route syntax, which panics at startup in 0.8. Asked &ldquo;How are
          path parameters like an id written in a route path in this version?&rdquo;, llmcontext
          answers: &ldquo;In axum, path parameters are written as segments in the route string
          enclosed in curly braces, for example, <Code>/users/{'{id}'}</Code>.&rdquo;
        </P>
      </Section>

      <Section id="limits" title="7. What it does not do yet">
        <P>
          <strong className="font-sans text-ink">Showing the new API is not always enough.</strong>{' '}
          In the axum runs, two models retrieved answers that used <Code>{'{id}'}</Code> and still
          wrote <Code>:id</Code>: an answer that only shows the current syntax does not overrule a
          confident memory. A guide should say what changed &mdash; &ldquo;in 0.8, routes use{' '}
          <Code>{'/{id}'}</Code>; <Code>/:id</Code> from 0.7 panics&rdquo; &mdash; and making
          answers flag differences from earlier versions is our next step.
        </P>
        <P>
          <strong className="font-sans text-ink">Agents have to be pointed at it.</strong> The gap
          between 22 and 31 runs of 35 is the cost of a vague instruction. Agent skills and tool
          descriptions carry that instruction today; better defaults in agents would carry it
          further.
        </P>
        <P>
          <strong className="font-sans text-ink">The evaluation is small.</strong> Seven prompts,
          one run each, scored by reading code. Repeated runs and executing the generated tests are
          what turn the direction above into a measured effect.
        </P>
        <P>
          <strong className="font-sans text-ink">The fully local path needs a recorded run.</strong>{' '}
          Every model role is configurable for in-network endpoints, but we have yet to publish an
          end-to-end run that indexes and queries a private repository with only local models.
        </P>
      </Section>

      <Section id="open" title="8. Open, as the plan">
        <P>
          A library people rely on to decide what counts as evidence should be one they can read,
          run, and change. We are working toward releasing llmcontext as open source. Querying
          public packages is already free and needs no API key.
        </P>
        <div className="flex flex-wrap gap-3 pt-2">
          <a
            href="/docs"
            className="border border-ink bg-ink px-5 py-2.5 text-sm font-semibold text-bg hover:opacity-90"
          >
            Get started
          </a>
          <a
            href="/search"
            className="border border-rule px-5 py-2.5 text-sm font-semibold text-ink hover:border-ink"
          >
            Browse public libraries
          </a>
        </div>
      </Section>

      <Section id="references" title="References">
        <ol className="list-decimal space-y-2 pl-5 text-sm text-link">
          <li>
            TrendAI, &ldquo;Slopsquatting: when AI agents hallucinate malicious packages.&rdquo;{' '}
            <a
              href={SLOPSQUATTING_RESEARCH_URL}
              target="_blank"
              rel="noreferrer"
              className="break-all text-ink underline underline-offset-4"
            >
              trendaisecurity.com
            </a>
          </li>
        </ol>
      </Section>
    </article>
  );
}
